import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { assignmentsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { errMsg } from '../lib/roles';

/** Landing page for emailed invitations: /invite/:token. */
export default function InvitePage() {
  const { token } = useParams();
  const { user, loading: authLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    assignmentsAPI.invitation(token).then(r => setInv(r.data)).catch(e => setError(errMsg(e)));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await assignmentsAPI.claim(token);
      toast.success('Invitation accepted!');
      if (checkAuth) await checkAuth();
      navigate(r.data.role === 'promoter' ? '/promoter' : r.data.role === 'dj' ? '/dj' : '/invitations');
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const redirect = encodeURIComponent(`/invite/${token}`);

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16 px-4">
      <div className="max-w-md mx-auto bg-[#0A0A0A] border border-gray-800 rounded-xl p-6 text-center">
        {error ? (
          <>
            <h1 className="text-xl font-black text-white mb-2">Invitation unavailable</h1>
            <p className="text-gray-400 text-sm mb-4">{error}</p>
            <Link to="/explore" className="btn-primary inline-block px-5 py-2 rounded text-sm">Explore events</Link>
          </>
        ) : !inv ? (
          <p className="text-gray-400">Loading…</p>
        ) : (
          <>
            <p className="text-xs font-bold tracking-widest text-red-500 uppercase mb-2">You're invited</p>
            <h1 className="text-2xl font-black text-white mb-1">{inv.event?.title}{inv.scope_type === 'series' ? ' (series)' : ''}</h1>
            <p className="text-gray-400 text-sm mb-4">
              {inv.assigned_by?.name} invited you as <span className="text-white font-semibold">{inv.role_label}</span>
              {inv.event?.date && inv.scope_type === 'event' ? ` on ${inv.event.date}` : ''}{inv.event?.city ? ` in ${inv.event.city}` : ''}.
            </p>
            {inv.campaign_name && <p className="text-gray-400 text-sm mb-4">Campaign: {inv.campaign_name}</p>}
            {authLoading ? null : user ? (
              <button onClick={accept} disabled={busy} className="btn-primary w-full py-3 rounded font-black disabled:opacity-60">
                {busy ? 'Accepting…' : `Accept as ${user.name}`}
              </button>
            ) : (
              <div className="space-y-2">
                <Link to={`/auth?tab=register&redirect=${redirect}${inv.role === 'promoter' ? '&type=promoter' : inv.role === 'dj' ? '&type=dj' : ''}`}
                  className="btn-primary block w-full py-3 rounded font-black">Create account & accept</Link>
                <Link to={`/auth?redirect=${redirect}`} className="block w-full py-3 rounded border border-gray-700 text-white text-sm font-bold">
                  I already have an account
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
